import posthog from 'posthog-js';

// Add org IDs here to enable socket event tracking for specific orgs only.
const TRACKED_ORG_IDS: string[] = [
  'org_LFzjnw7VlZ', // University living
  'org_DsSMSKYvvB', // Bajaj Finserv
];

// Add org IDs here to suppress the connection-lost bar in the UI for specific orgs.
// Socket reconnection logic still runs — only the bar is hidden.
const HIDE_CONNECTION_LOST_BAR_ORG_IDS: string[] = [
  'org_DsSMSKYvvB', // Bajaj Finserv
];

let trackingEnabled = false;
let currentOrgId = '';

export const initSocketEventActionTypeTracking = (orgId: string): void => {
  trackingEnabled = TRACKED_ORG_IDS.includes(orgId);
  currentOrgId = orgId;
};

export const isConnectionLostBarSuppressed = (): boolean => HIDE_CONNECTION_LOST_BAR_ORG_IDS.includes(currentOrgId);

export enum SocketEventActionType {
  SOCKET_PING_SENT = 'socket_ping_sent',
  SOCKET_PONG_RECEIVED = 'socket_pong_received',
  SOCKET_RETRY_SCHEDULED = 'socket_retry_scheduled',
  SOCKET_CONNECTION_LOST = 'socket_connection_lost',
  SOCKET_CONNECTION_RECOVERED = 'socket_connection_recovered',
  SOCKET_DISCONNECTED = 'socket_disconnected',
  SOCKET_RECONNECTED = 'socket_reconnected',
  SOCKET_TAB_VISIBILITY_CHANGED = 'socket_tab_visibility_changed',
  CONNECTION_LOST_BAR_SHOWN = 'connection_lost_bar_shown',
  CONNECTION_LOST_BAR_HIDDEN = 'connection_lost_bar_hidden',
}

type SocketEventActionTypeAction =
  | { type: SocketEventActionType.SOCKET_PING_SENT; socketName: string; retryCount: number }
  | { type: SocketEventActionType.SOCKET_PONG_RECEIVED; socketName: string; retryCount: number }
  | { type: SocketEventActionType.SOCKET_RETRY_SCHEDULED; socketName: string; retryCount: number; delayMs: number }
  | { type: SocketEventActionType.SOCKET_CONNECTION_LOST; socketName: string; retryCount: number }
  | {
      type: SocketEventActionType.SOCKET_CONNECTION_RECOVERED;
      socketName: string;
      via: 'pong' | 'any_message';
      retryCount: number;
    }
  | { type: SocketEventActionType.SOCKET_DISCONNECTED; socketName: string; reason: string }
  | { type: SocketEventActionType.SOCKET_RECONNECTED; socketName: string }
  | { type: SocketEventActionType.SOCKET_TAB_VISIBILITY_CHANGED; socketName: string; state: DocumentVisibilityState }
  | { type: SocketEventActionType.CONNECTION_LOST_BAR_SHOWN }
  | { type: SocketEventActionType.CONNECTION_LOST_BAR_HIDDEN };

const getBaseProperties = (socketName: string) => ({
  org_id: currentOrgId,
  socket_name: socketName,
  tab_visible: document.visibilityState === 'visible',
  online: navigator.onLine,
  timestamp: new Date().toISOString(),
});

const getBarProperties = () => ({
  org_id: currentOrgId,
  tab_visible: document.visibilityState === 'visible',
  online: navigator.onLine,
  timestamp: new Date().toISOString(),
});

export const trackSocketEventActionType = (action: SocketEventActionTypeAction): void => {
  if (!trackingEnabled) return;

  switch (action.type) {
    case SocketEventActionType.SOCKET_PING_SENT:
      posthog.capture(action.type, {
        ...getBaseProperties(action.socketName),
        retry_count: action.retryCount,
        is_retry: action.retryCount > 0,
      });
      break;

    case SocketEventActionType.SOCKET_PONG_RECEIVED:
      posthog.capture(action.type, {
        ...getBaseProperties(action.socketName),
        retry_count: action.retryCount,
      });
      break;

    case SocketEventActionType.SOCKET_RETRY_SCHEDULED:
      posthog.capture(action.type, {
        ...getBaseProperties(action.socketName),
        retry_count: action.retryCount,
        delay_ms: action.delayMs,
      });
      break;

    case SocketEventActionType.SOCKET_CONNECTION_LOST:
      posthog.capture(action.type, {
        ...getBaseProperties(action.socketName),
        retry_count: action.retryCount,
      });
      break;

    case SocketEventActionType.SOCKET_CONNECTION_RECOVERED:
      posthog.capture(action.type, {
        ...getBaseProperties(action.socketName),
        recovered_via: action.via,
        retry_count_at_recovery: action.retryCount,
      });
      break;

    case SocketEventActionType.SOCKET_DISCONNECTED:
      posthog.capture(action.type, {
        ...getBaseProperties(action.socketName),
        reason: action.reason,
      });
      break;

    case SocketEventActionType.SOCKET_RECONNECTED:
      posthog.capture(action.type, getBaseProperties(action.socketName));
      break;

    case SocketEventActionType.SOCKET_TAB_VISIBILITY_CHANGED:
      posthog.capture(action.type, {
        ...getBaseProperties(action.socketName),
        visibility_state: action.state,
      });
      break;

    case SocketEventActionType.CONNECTION_LOST_BAR_SHOWN:
      posthog.capture(action.type, getBarProperties());
      break;

    case SocketEventActionType.CONNECTION_LOST_BAR_HIDDEN:
      posthog.capture(action.type, getBarProperties());
      break;
  }
};
